import { useEffect, useRef, useState } from 'react';

import { isAdditiveSelectEvent } from '../utils/isAdditiveSelectEvent';

const isEditableTarget = (target) => {
  if (!target) {
    return false;
  }
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') {
    return true;
  }
  if (target.isContentEditable) {
    return true;
  }
  if (typeof target.closest === 'function' && target.closest('[role="button"]')) {
    return true;
  }
  return false;
};

const getClientPoint = (evt) => {
  const touch = evt.touches?.[0] || evt.changedTouches?.[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : { x: evt.clientX, y: evt.clientY };
};

const useCanvasPan = ({ isHandTool, isCropMode, editingId, multiSelect = false, onSelectElement, onPanBy }) => {
  const [spacePan, setSpacePan] = useState(false);
  const spacePanRef = useRef(false);
  const isPanningRef = useRef(false);
  const lastPointerRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== 'Space' || editingId) {
        return;
      }

      if (isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      if (!event.repeat) {
        spacePanRef.current = true;
        setSpacePan(true);
      }
    };
    const handleKeyUp = (event) => {
      if (event.code === 'Space') {
        spacePanRef.current = false;
        isPanningRef.current = false;
        setSpacePan(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [editingId]);

  const canPan = () => isHandTool || spacePanRef.current;

  const handlePointerDown = (event) => {
    if (editingId) {
      return false;
    }

    if (canPan()) {
      isPanningRef.current = true;
      lastPointerRef.current = getClientPoint(event.evt);
      return true;
    }

    const isStage = event.target === event.target.getStage();
    if (!isCropMode && isStage && !(multiSelect && isAdditiveSelectEvent(event.evt))) {
      onSelectElement(null);
    }
    return false;
  };

  const handlePointerMove = (event) => {
    if (!isPanningRef.current || !lastPointerRef.current || !onPanBy) {
      return;
    }
    const point = getClientPoint(event.evt);
    const dx = point.x - lastPointerRef.current.x;
    const dy = point.y - lastPointerRef.current.y;
    lastPointerRef.current = point;
    onPanBy(dx, dy);
  };

  const handlePointerUp = () => {
    isPanningRef.current = false;
    lastPointerRef.current = null;
  };

  let cursor = 'default';
  if (canPan() || spacePan || isPanningRef.current) {
    cursor = isPanningRef.current ? 'grabbing' : 'grab';
  }

  return {
    cursor,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
};

export default useCanvasPan;
